import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// QB Mapping:
// estimate.estimate_number → DocNumber
// estimate.customer_id → CustomerRef.value
// estimate.txn_date → TxnDate
// estimate.expiry_date → ExpiryDate
// estimate.notes → CustomerMemo
// estimate.total_amount → TotalAmt
//
// Each SubLine flattens to one QB SalesItemLineDetail:
//   SubLine.description → Line[].Description
//   SubLine.qty → Line[].SalesItemLineDetail.Qty
//   SubLine.unit_price → Line[].SalesItemLineDetail.UnitPrice (billed price)
//   SubLine.qb_tax_code → Line[].SalesItemLineDetail.TaxCodeRef ('NON' for labor, 'TAX' for material)
//   SubLine.qb_item_ref → Line[].SalesItemLineDetail.ItemRef ('Installation Labor' | 'Job Materials')

export type QuantityMode = "auto" | "manual" | "formula";

export interface QuantityFormula {
  kind: "coverage" | "multiplier";
  source: "parent_qty";
  source_unit: string;
  coverage_per_unit?: number;
  multiplier?: number;
  round_mode?: "none" | "up" | "down" | "nearest";
  min_qty?: number;
}

/**
 * Pure formula computation. Single source of truth for normalization, UI preview, and tests.
 * Anchored to parent unit, NOT trigger labor unit.
 */
export function applyFormula(parentQty: number, parentUnit: string, formula: QuantityFormula): number {
  if (!formula.source_unit || parentUnit !== formula.source_unit) return 0;

  let result: number;
  if (formula.kind === "coverage") {
    const coverage = formula.coverage_per_unit ?? 0;
    if (coverage <= 0) return 0;
    result = parentQty / coverage;
  } else {
    const multiplier = formula.multiplier ?? 0;
    if (!Number.isFinite(multiplier) || multiplier < 0) return 0;
    result = parentQty * multiplier;
  }

  switch (formula.round_mode) {
    case "up": result = Math.ceil(result); break;
    case "down": result = Math.floor(result); break;
    case "nearest": result = Math.round(result); break;
    default: break;
  }

  if (formula.min_qty != null && result < formula.min_qty) {
    result = formula.min_qty;
  }

  return Number.isFinite(result) && result >= 0 ? result : 0;
}

export interface SubLine {
  id: string;
  type: "labor" | "material";
  description: string;
  qty: number;
  unit: string;
  cost_price: number;       // true cost — used for WO/PO assignment
  markup_pct: number;       // default 35
  unit_price: number;       // cost × (1 + markup/100) — billed to customer
  total_cost: number;       // qty × cost_price
  total_billed: number;     // qty × unit_price
  taxable: boolean;         // false for labor, true (default) for material
  qb_tax_code: "NON" | "TAX";
  qb_item_ref: string;
  product_service_id?: string;
  income_account?: string;
  cogs_account?: string;
  sort_order?: number;
  quantity_mode?: QuantityMode;
  quantity_formula?: QuantityFormula | null;
  apply_vendor_tax?: boolean;
  vendor_tax_rate?: number;
  // backward compat: old estimates may have `total` instead of `total_billed`
  total?: number;
}

export interface LineItem {
  line_num: number;
  label?: string;                 // optional plan reference code (e.g. FT-01, WT-4)
  description: string;
  customer_description: string;  // merged description for customer view
  qty: number;
  unit: string;
  sub_lines: SubLine[];
  labor_total_cost: number;
  labor_total_billed: number;
  material_total_cost: number;
  material_total_billed: number;
  total_cost: number;
  total_billed: number;
  sort_order?: number;
  group_id?: string | null;      // links to EstimateGroup.id; null = ungrouped
  // backward compat
  labor_total?: number;
  material_total?: number;
  total?: number;
}

export interface EstimateGroup {
  id: string;
  name: string;
  group_type: "room" | "phase" | "trade" | "custom";
  sort_order: number;
  color?: string;
}

export interface EstimatePrintSettings {
  show_unit_prices: boolean;
  show_quantities: boolean;
  show_line_items: boolean;
  show_labor_material_breakdown: boolean;
  show_group_subtotals_only: boolean;
  show_tax: boolean;
  show_notes: boolean;
  show_expiry: boolean;
  show_scope_summary: boolean;
}

export const DEFAULT_PRINT_SETTINGS: EstimatePrintSettings = {
  show_unit_prices: true,
  show_quantities: true,
  show_line_items: true,
  show_labor_material_breakdown: false,
  show_group_subtotals_only: false,
  show_tax: true,
  show_notes: true,
  show_expiry: true,
  show_scope_summary: false,
};

// Legacy flat format (backward compat for old estimates)
export interface LegacyLineItem {
  description: string;
  line_type: "labor" | "material" | "both";
  qty: number;
  unit: string;
  unit_price: number;
  labor_price: number;
  material_price: number;
  total: number;
}

export type JobType = 'flooring' | 'roofing' | 'emergency' | 'general' | 'other';

export interface Estimate {
  id: string;
  company_id: string;
  estimate_number: string | null;
  customer_id: string | null;
  txn_date: string;
  expiry_date: string | null;
  status: string;
  job_type: JobType;
  line_items: LineItem[] | LegacyLineItem[];
  estimate_groups?: EstimateGroup[];
  print_settings?: EstimatePrintSettings;
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  total_amount: number;
  notes: string | null;
  project_name: string | null;
  qb_estimate_id: string | null;
  qb_sync_status: string | null;
  qb_synced_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  customers?: { name: string } | null;
  // Revision fields
  parent_estimate_id: string | null;
  revision_number: number;
  revision_notes: string | null;
  reference_plan_document_id: string | null;
}

export function isNewFormat(item: any): item is LineItem {
  return Array.isArray(item?.sub_lines);
}

/** Get the billed total from a line item (handles old and new format) */
function getLineBilled(li: LineItem): number {
  return li.total_billed ?? li.total ?? 0;
}

/** Derive sov_line fields from a LineItem for contract creation */
export function lineItemToSovFields(li: LineItem) {
  const qty = li.qty || 1;
  const laborBilled = li.labor_total_billed ?? li.labor_total ?? 0;
  const materialBilled = li.material_total_billed ?? li.material_total ?? 0;
  const totalBilled = li.total_billed ?? li.total ?? 0;
  const totalCost = li.total_cost ?? 0;
  const laborUp = laborBilled / qty;
  const materialUp = materialBilled / qty;
  const hasLabor = li.sub_lines.some((sl) => sl.type === "labor");
  const hasMaterial = li.sub_lines.some((sl) => sl.type === "material");
  const lineType = hasLabor && hasMaterial ? "both" : hasLabor ? "labor" : "materials";

  return {
    description: li.description,
    quantity: qty,
    unit: li.unit,
    labor_unit_price: laborUp,
    material_unit_price: materialUp,
    unit_price: laborUp + materialUp,
    scheduled_value: totalBilled,
    line_type: lineType,
    sub_lines: li.sub_lines,
    total_cost: totalCost,
    total_billed: totalBilled,
  };
}

/** Generate a crypto UUID (browser-safe) */
function uuid(): string {
  return crypto.randomUUID();
}

/** Build a SubLine with defaults. Accepts optional parent context for auto-fill. */
export function makeSubLine(
  type: "labor" | "material",
  partial?: Partial<SubLine>,
  parentContext?: { qty?: number; unit?: string; description?: string }
): SubLine {
  const cost_price = partial?.cost_price ?? 0;
  const markup_pct = partial?.markup_pct ?? (type === "labor" ? 35 : 20);
  const unit_price = partial?.unit_price ?? (cost_price > 0 ? cost_price * (1 + markup_pct / 100) : 0);
  const qty = partial?.qty ?? parentContext?.qty ?? 1;
  const unit = partial?.unit ?? parentContext?.unit ?? "SF";
  const taxable = partial?.taxable ?? (type === "material");
  const desc = partial?.description ?? (parentContext?.description
    ? (type === "labor" ? `${parentContext.description} - Labor` : parentContext.description)
    : "");
  return {
    id: uuid(),
    type,
    description: desc,
    qty,
    unit,
    cost_price,
    markup_pct,
    unit_price,
    total_cost: qty * cost_price,
    total_billed: qty * unit_price,
    taxable,
    qb_tax_code: taxable ? "TAX" : "NON",
    qb_item_ref: type === "labor" ? "Installation Labor" : "Job Materials",
    sort_order: partial?.sort_order ?? 0,
    quantity_mode: partial?.quantity_mode ?? "auto",
    quantity_formula: partial?.quantity_formula ?? null,
    ...partial,
  };
}

/** Build a LineItem with defaults */
export function makeLineItem(lineNum: number, groupId?: string | null): LineItem {
  return {
    line_num: lineNum,
    label: "",
    description: "",
    customer_description: "",
    qty: 1,
    unit: "SF",
    sub_lines: [makeSubLine("labor", { sort_order: 0, quantity_mode: "auto" })],
    labor_total_cost: 0,
    labor_total_billed: 0,
    material_total_cost: 0,
    material_total_billed: 0,
    total_cost: 0,
    total_billed: 0,
    sort_order: 0,
    group_id: groupId ?? null,
  };
}

/** Organize line items into groups for rendering/printing */
export function groupLineItems(
  lines: LineItem[],
  groups?: EstimateGroup[],
): { group: EstimateGroup | null; lines: LineItem[] }[] {
  if (!groups || groups.length === 0) {
    return [{ group: null, lines }];
  }
  const sorted = [...groups].sort((a, b) => a.sort_order - b.sort_order);
  const result: { group: EstimateGroup | null; lines: LineItem[] }[] = sorted.map((g) => ({
    group: g,
    lines: lines.filter((li) => li.group_id === g.id),
  }));
  const ungrouped = lines.filter((li) => !li.group_id || !groups.some((g) => g.id === li.group_id));
  if (ungrouped.length > 0) {
    result.push({ group: null, lines: ungrouped });
  }
  return result;
}

/** Sum totals for all lines in a specific group */
export function recalcGroupTotals(lines: LineItem[], groupId: string) {
  const grouped = lines.filter((li) => li.group_id === groupId);
  return {
    labor_cost: grouped.reduce((s, li) => s + (li.labor_total_cost ?? 0), 0),
    labor_billed: grouped.reduce((s, li) => s + (li.labor_total_billed ?? li.labor_total ?? 0), 0),
    material_cost: grouped.reduce((s, li) => s + (li.material_total_cost ?? 0), 0),
    material_billed: grouped.reduce((s, li) => s + (li.material_total_billed ?? li.material_total ?? 0), 0),
    total_cost: grouped.reduce((s, li) => s + (li.total_cost ?? 0), 0),
    total_billed: grouped.reduce((s, li) => s + (li.total_billed ?? li.total ?? 0), 0),
  };
}

/** Recalculate LineItem totals from its sub_lines */
export function recalcLineItem(li: LineItem): LineItem {
  let laborTotalCost = 0;
  let laborTotalBilled = 0;
  let materialTotalCost = 0;
  let materialTotalBilled = 0;

  const sub_lines = li.sub_lines.map((sl) => {
    const total_cost = sl.qty * sl.cost_price;
    const total_billed = sl.qty * sl.unit_price;
    if (sl.type === "labor") {
      laborTotalCost += total_cost;
      laborTotalBilled += total_billed;
    } else {
      materialTotalCost += total_cost;
      materialTotalBilled += total_billed;
    }
    return { ...sl, total_cost, total_billed, total: total_billed };
  });

  return {
    ...li,
    sub_lines,
    labor_total_cost: laborTotalCost,
    labor_total_billed: laborTotalBilled,
    material_total_cost: materialTotalCost,
    material_total_billed: materialTotalBilled,
    total_cost: laborTotalCost + materialTotalCost,
    total_billed: laborTotalBilled + materialTotalBilled,
    // backward compat
    labor_total: laborTotalBilled,
    material_total: materialTotalBilled,
    total: laborTotalBilled + materialTotalBilled,
  };
}

/**
 * Find the trigger unit for a parent line.
 * The trigger unit is the unit of the first labor sub-line sorted by sort_order.
 */
export function getLineTriggerUnit(subLines: SubLine[]): string | null {
  const sorted = [...subLines].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const firstLabor = sorted.find((sl) => sl.type === "labor");
  return firstLabor?.unit ?? null;
}

/**
 * Normalize an array of LineItems with deterministic quantity propagation and sort ordering.
 *
 * Precedence:
 * 1. manual => preserve qty exactly
 * 2. formula + valid quantity_formula => compute via applyFormula(parent.qty, parent.unit, formula)
 * 3. formula + missing/invalid formula => keep mode formula, qty = 0
 * 4. auto => trigger-unit logic:
 *    - no trigger => 0
 *    - matching unit => parent qty
 *    - non-matching => 0
 *
 * Formula rows do NOT depend on trigger-unit. They compute from parent qty/unit directly.
 */
export function normalizeEstimateLineItems(lines: LineItem[]): LineItem[] {
  return lines.map((line, lineIdx) => {
    const normalizedLine = {
      ...line,
      sort_order: line.sort_order ?? lineIdx,
    };

    const subLinesWithOrder = normalizedLine.sub_lines.map((sl, slIdx) => ({
      ...sl,
      sort_order: sl.sort_order ?? slIdx,
      quantity_mode: (sl.quantity_mode ?? "auto") as QuantityMode,
    }));

    const triggerUnit = getLineTriggerUnit(subLinesWithOrder);

    const normalizedSubLines = subLinesWithOrder.map((sl) => {
      // 1. manual => preserve qty exactly
      if (sl.quantity_mode === "manual") return sl;

      // 2. formula => compute from parent qty/unit
      if (sl.quantity_mode === "formula") {
        if (sl.quantity_formula) {
          const computed = applyFormula(normalizedLine.qty, normalizedLine.unit, sl.quantity_formula);
          return { ...sl, qty: computed };
        }
        // formula mode but missing/invalid formula => qty 0, keep mode as formula
        return { ...sl, qty: 0 };
      }

      // 3. auto => trigger-unit logic
      if (!triggerUnit) {
        return { ...sl, qty: 0 };
      }
      if (sl.unit === triggerUnit) {
        return { ...sl, qty: normalizedLine.qty };
      }
      return { ...sl, qty: 0 };
    });

    return recalcLineItem({
      ...normalizedLine,
      sub_lines: normalizedSubLines,
    });
  });
}

export function useEstimates() {
  return useQuery<Estimate[]>({
    queryKey: ["estimates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("estimates")
        .select("*, customers(name)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as Estimate[];
    },
  });
}

interface CreateEstimateInput {
  customer_id: string | null;
  txn_date: string;
  expiry_date: string | null;
  notes: string | null;
  line_items: LineItem[];
  estimate_groups?: EstimateGroup[];
  print_settings?: EstimatePrintSettings;
  subtotal: number;
  tax_rate?: number;
  tax_amount?: number;
  total_amount: number;
  qb_sync_status: string;
  job_type?: JobType;
  project_id?: string | null;
  project_name?: string | null;
  is_change_order?: boolean;
}

export function useCreateEstimate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateEstimateInput) => {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");

      // Normalize before persist
      const normalizedLines = normalizeEstimateLineItems(input.line_items);
      const subtotal = normalizedLines.reduce((s, l) => s + (l.total_billed ?? 0), 0);

      const taxAmount = input.tax_amount ?? 0;
      const totalWithTax = subtotal + taxAmount;

      const insertObj: Record<string, any> = {
          company_id: companyId,
          customer_id: input.customer_id || null,
          txn_date: input.txn_date,
          expiry_date: input.expiry_date || null,
          notes: input.notes || null,
          line_items: normalizedLines as any,
          estimate_groups: input.estimate_groups ?? [],
          print_settings: input.print_settings ?? null,
          subtotal: subtotal,
          tax_rate: input.tax_rate ?? 0,
          tax_amount: taxAmount,
          total_amount: totalWithTax,
          qb_sync_status: input.qb_sync_status,
          job_type: input.job_type || 'general',
      };
      if (input.project_id) insertObj.project_id = input.project_id;
      if (input.project_name !== undefined) insertObj.project_name = input.project_name || null;
      if (input.is_change_order) insertObj.is_change_order = true;

      const { data, error } = await supabase
        .from("estimates")
        .insert(insertObj as any)
        .select("*")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["estimates"] });
    },
  });
}

export function useUpdateEstimate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: Partial<CreateEstimateInput> & { id: string; status?: string }) => {
      const updateObj: Record<string, any> = {};
      if (input.customer_id !== undefined) updateObj.customer_id = input.customer_id || null;
      if (input.txn_date !== undefined) updateObj.txn_date = input.txn_date;
      if (input.expiry_date !== undefined) updateObj.expiry_date = input.expiry_date || null;
      if (input.notes !== undefined) updateObj.notes = input.notes || null;
      if (input.line_items !== undefined) {
        // Normalize before persist
        const normalizedLines = normalizeEstimateLineItems(input.line_items);
        updateObj.line_items = normalizedLines as any;
        const subtotal = normalizedLines.reduce((s, l) => s + (l.total_billed ?? 0), 0);
        const taxAmount = input.tax_amount ?? 0;
        updateObj.subtotal = subtotal;
        updateObj.tax_rate = input.tax_rate ?? 0;
        updateObj.tax_amount = taxAmount;
        updateObj.total_amount = subtotal + taxAmount;
      } else {
        if (input.subtotal !== undefined) updateObj.subtotal = input.subtotal;
        if (input.total_amount !== undefined) updateObj.total_amount = input.total_amount;
        if (input.tax_rate !== undefined) updateObj.tax_rate = input.tax_rate;
        if (input.tax_amount !== undefined) updateObj.tax_amount = input.tax_amount;
      }
      if (input.qb_sync_status !== undefined) updateObj.qb_sync_status = input.qb_sync_status;
      if (input.job_type !== undefined) updateObj.job_type = input.job_type;
      if (input.estimate_groups !== undefined) updateObj.estimate_groups = input.estimate_groups;
      if (input.print_settings !== undefined) updateObj.print_settings = input.print_settings;
      if ((input as any).status !== undefined) updateObj.status = (input as any).status;
      if ((input as any).project_name !== undefined) updateObj.project_name = (input as any).project_name || null;

      const { data, error } = await supabase
        .from("estimates")
        .update(updateObj)
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ["estimates"] });
      qc.invalidateQueries({ queryKey: ["estimate_detail"] });
      if (data?.id) {
        qc.setQueryData(["estimate_detail", data.id], (prev: any) =>
          prev ? { ...prev, ...data } : data
        );
        qc.invalidateQueries({ queryKey: ["estimate_detail", data.id] });
      }
    },
  });
}

export function useDuplicateEstimate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      sourceEstimate,
      customer_id,
      project_name,
      job_type,
    }: {
      sourceEstimate: Estimate;
      customer_id: string;
      project_name?: string | null;
      job_type?: JobType;
    }) => {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");

      const insertObj: Record<string, any> = {
        company_id: companyId,
        customer_id,
        txn_date: new Date().toISOString().split("T")[0],
        expiry_date: sourceEstimate.expiry_date,
        notes: sourceEstimate.notes,
        line_items: sourceEstimate.line_items as any,
        estimate_groups: sourceEstimate.estimate_groups ?? [],
        print_settings: sourceEstimate.print_settings ?? null,
        subtotal: sourceEstimate.subtotal,
        tax_rate: sourceEstimate.tax_rate,
        tax_amount: sourceEstimate.tax_amount,
        total_amount: sourceEstimate.total_amount,
        qb_sync_status: "not_synced",
        job_type: job_type ?? sourceEstimate.job_type,
        project_name: project_name ?? sourceEstimate.project_name,
        status: "Draft",
      };

      const { data, error } = await supabase
        .from("estimates")
        .insert(insertObj as any)
        .select("*")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["estimates"] });
    },
  });
}

export function useCreateRevision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      sourceEstimate,
      revisionNotes,
      referencePlanDocumentId,
    }: {
      sourceEstimate: Estimate;
      revisionNotes: string;
      referencePlanDocumentId: string | null;
    }) => {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");

      // Determine the root parent
      const rootId = sourceEstimate.parent_estimate_id ?? sourceEstimate.id;

      // Find max revision number among siblings
      const { data: siblings } = await supabase
        .from("estimates")
        .select("revision_number")
        .or(`id.eq.${rootId},parent_estimate_id.eq.${rootId}`)
        .order("revision_number", { ascending: false })
        .limit(1);

      const maxRev = (siblings && siblings.length > 0)
        ? (siblings[0] as any).revision_number ?? 0
        : sourceEstimate.revision_number ?? 0;

      const newRevNumber = maxRev + 1;

      const insertObj: Record<string, any> = {
        company_id: companyId,
        customer_id: sourceEstimate.customer_id,
        txn_date: new Date().toISOString().split("T")[0],
        expiry_date: sourceEstimate.expiry_date,
        notes: sourceEstimate.notes,
        line_items: sourceEstimate.line_items as any,
        estimate_groups: sourceEstimate.estimate_groups ?? [],
        print_settings: sourceEstimate.print_settings ?? null,
        subtotal: sourceEstimate.subtotal,
        tax_rate: sourceEstimate.tax_rate,
        tax_amount: sourceEstimate.tax_amount,
        total_amount: sourceEstimate.total_amount,
        qb_sync_status: "not_synced",
        job_type: sourceEstimate.job_type,
        project_name: sourceEstimate.project_name,
        status: "Draft",
        parent_estimate_id: rootId,
        revision_number: newRevNumber,
        revision_notes: revisionNotes || null,
        reference_plan_document_id: referencePlanDocumentId || null,
      };

      const { data, error } = await supabase
        .from("estimates")
        .insert(insertObj as any)
        .select("*")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["estimates"] });
      qc.invalidateQueries({ queryKey: ["estimate_detail"] });
    },
  });
}